import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { RouterFacade } from '@core/shared/data-access';
import { NotificationService, QueryParams, filterNull, getProp } from '@core/shared/util';
import {
  PlatformPackagingUnit,
  PlatformPackagingUnitsService,
  UpdatePlatformPackagingUnit,
} from '@mp/system/platform-packaging-units/data-access';

@Injectable({ providedIn: 'root' })
export class PlatformPackagingUnitsFacade {
  private readonly _packagingUnits$ = new BehaviorSubject([] as Array<PlatformPackagingUnit>);
  readonly packagingUnits$ = this._packagingUnits$.asObservable();

  private readonly _packagingUnit$ = new BehaviorSubject(undefined as PlatformPackagingUnit | undefined);
  readonly packagingUnit$ = this._packagingUnit$.asObservable();

  readonly urlShortName$: Observable<string>;
  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);

  get isLoading$(): Observable<boolean> {
    return this._isLoading$.asObservable();
  }

  constructor(
    private readonly service: PlatformPackagingUnitsService,
    private readonly routerFacade: RouterFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toaster: NotificationService,
  ) {
    this.urlShortName$ = this.routerFacade.routeParam$(':shortName').pipe(filterNull());
  }

  getAll(searchTerm?: string): void {
    this._isLoading$.next(true);
    const params: HttpParams = this.setQueryParams(searchTerm);

    this.service
      .getAllPlatformPackagingUnits(params)
      .pipe(getProp('data'))
      .subscribe({
        next: (unit) => {
          this._packagingUnits$.next(unit);
          this._isLoading$.next(false);
        },
      });
  }

  private setQueryParams(searchTerm?: string): HttpParams {
    const queryParams = QueryParams.build<PlatformPackagingUnit>();

    // if (searchTerm) { queryParams.filter(['shortName', 'longName' as keyof PlatformPackagingUnit], '@=*', searchTerm); }
    if (searchTerm) {
      queryParams.param('searchTerm', searchTerm);
    }
    queryParams.sort('longName');
    return queryParams.toHttpParams();
  }

  get(shortName: string): void {
    this._isLoading$.next(true);
    this.service
      .getPlatformPackagingUnit(shortName)
      .pipe(getProp('data'))
      .subscribe({
        next: (unit) => {
          this._packagingUnit$.next(unit);
          this._isLoading$.next(false);
        },
      });
  }

  createUnit(unit: PlatformPackagingUnit): void {
    this._isLoading$.next(true);
    this.service
      .createUnit(unit)
      .pipe(getProp('data'))
      .subscribe({
        next: (response) => {
          this._isLoading$.next(false);
          this.getAll();
          if (response.statusCode === 200) this.toaster.toastSuccess(response.statusMessage);
          else if (response.statusCode === 400) this.toaster.toastDanger(response.statusMessage);
          this.navigateBack();
        },
      });
  }

  updateUnit(updateDto: UpdatePlatformPackagingUnit): void {
    this._isLoading$.next(true);
    this.service.updateUnit(updateDto).subscribe({
      next: () => {
        this._isLoading$.next(false);
        this.get(updateDto.shortName);
        this.getAll();
        this.toaster.toastSuccess('Die Plattformeinheit wurde aktualisiert.');
        this.navigateBack();
      },
    });
  }

  deleteUnit(shortName: string): void {
    this.service.deleteUnit(shortName).subscribe({
      next: () => {
        this._isLoading$.next(false);
        this.getAll();
        this.toaster.toastSuccess('Die Plattformeinheit wurde gelöscht.');
        this.navigateBack();
      },
    });
  }

  navigateBack(): void {
    this.router.navigate(['./system/platform-packaging-units'], {
      relativeTo: this.route,
    });
  }
}
